import { Rocket, Target, Users, BarChart3, Zap, TrendingUp, Megaphone, Settings, LineChart, Crosshair } from "lucide-react";
import SubServicePageLayout from "@/components/SubServicePageLayout";

const services = [
  {
    name: "Demand Generation",
    icon: Target,
    components: [
      "Paid acquisition across LinkedIn, Google, Meta",
      "Content syndication and ABM",
      "Webinar and event marketing",
    ],
    outcome: "Consistent pipeline of qualified opportunities.",
  },
  {
    name: "Lead Qualification",
    icon: Users,
    components: [
      "Lead scoring and routing",
      "MQL to SQL conversion optimization",
      "Sales and marketing alignment",
    ],
    outcome: "Higher quality leads that convert to customers.",
  },
  {
    name: "Conversion Optimization",
    icon: TrendingUp,
    components: [
      "Demo booking optimization",
      "Free trial conversion programs",
      "Pricing and packaging tests",
    ],
    outcome: "Faster sales cycles and higher win rates.",
  },
  {
    name: "Revenue Analytics",
    icon: BarChart3,
    components: [
      "Full-funnel attribution",
      "CAC payback and LTV analysis",
      "Cohort-based forecasting",
    ],
    outcome: "Clear visibility into revenue drivers.",
  },
  {
    name: "Product-Led Growth",
    icon: Zap,
    components: [
      "In-app onboarding optimization",
      "Usage-based activation triggers",
      "Expansion revenue programs",
    ],
    outcome: "Turn users into advocates and upsell opportunities.",
  },
  {
    name: "Retention & Expansion",
    icon: Rocket,
    components: [
      "Churn prediction and prevention",
      "Customer health scoring",
      "Upsell and cross-sell campaigns",
    ],
    outcome: "Maximize net revenue retention.",
  },
];

const processSteps = [
  {
    number: "01",
    title: "Funnel Audit",
    description: "Map your entire customer journey from first touch to expansion, identifying gaps and opportunities.",
  },
  {
    number: "02",
    title: "Strategy Development",
    description: "Build a demand generation strategy aligned with your ICP, sales motion, and growth targets.",
  },
  {
    number: "03",
    title: "Campaign Launch",
    description: "Execute multi-channel campaigns with proper tracking, creative, and targeting.",
  },
  {
    number: "04",
    title: "Pipeline Optimization",
    description: "Continuously improve conversion rates at every stage of the funnel.",
  },
  {
    number: "05",
    title: "Scale & Predict",
    description: "Build predictable growth models and scale winning strategies.",
  },
];

const technologyCategories = [
  { title: "Advertising", icon: Megaphone, items: ["LinkedIn Ads", "Google Ads", "Meta Ads", "G2/Capterra"] },
  { title: "Marketing Automation", icon: Settings, items: ["HubSpot", "Marketo", "Pardot", "Customer.io"] },
  { title: "Analytics", icon: LineChart, items: ["Segment", "Amplitude", "Mixpanel", "HockeyStack"] },
  { title: "ABM & Intent", icon: Crosshair, items: ["6sense", "Demandbase", "Bombora", "Clearbit"] },
];

const faqs = [
  {
    question: "What stage SaaS companies do you work with?",
    answer: "We work with Series A to Series D companies, typically $2M-$100M ARR. We're best suited for companies with product-market fit looking to scale efficiently.",
  },
  {
    question: "How do you handle the long B2B sales cycle?",
    answer: "We build full-funnel attribution and focus on leading indicators like MQL quality and demo rates, while optimizing for downstream revenue impact.",
  },
  {
    question: "Do you work with product-led and sales-led companies?",
    answer: "Yes. Many companies are hybrid. We adapt our approach based on your primary motion and help integrate PLG and SLG strategies where appropriate.",
  },
  {
    question: "How do you measure success?",
    answer: "We align on metrics that matter to your business: pipeline generated, CAC payback, LTV:CAC ratio, and revenue influenced. Vanity metrics are secondary.",
  },
  {
    question: "What's your relationship with sales teams?",
    answer: "We believe marketing and sales alignment is critical. We work closely with sales leadership to ensure leads are qualified and feedback loops are established.",
  },
];

const qualification = {
  forYou: [
    "You're post-product-market fit and ready to scale",
    "You have at least $2M ARR and a defined sales motion",
    "You understand that sustainable growth requires systems",
    "You're willing to invest in marketing as a revenue center",
  ],
  notForYou: [
    "You're still finding product-market fit",
    "You expect leads without budget for demand generation",
    "You're looking for short-term hacks, not scalable systems",
    "You view marketing as a cost center rather than growth driver",
  ],
};

const SaaSAcceleration = () => {
  return (
    <SubServicePageLayout
      seoTitle="SaaS Acceleration Services"
      seoDescription="Demand generation and conversion optimization for software companies. From MQL to customer. Pipeline growth, reduced churn, faster sales cycles."
      canonicalUrl="https://xperfomax.com/solutions/saas-acceleration"
      category="solutions"
      categoryLabel="Solutions / SaaS Acceleration"
      headline="SaaS growth, systematized."
      subheadline="We help B2B software companies build predictable demand generation engines that drive qualified pipeline and accelerate revenue."
      problemTitle="Why most SaaS growth plateaus."
      problems={[
        "Demand generation is misaligned with sales motion and ICP.",
        "MQL volume grows but revenue impact stays flat.",
        "Attribution is broken, so optimization decisions are guesses.",
        "Retention and expansion are afterthoughts, not growth levers.",
      ]}
      systemTitle="A complete growth system for SaaS."
      systemDescription="Integrated demand generation, conversion optimization, and retention strategies."
      services={services}
      processTitle="From audit to predictable growth."
      processSteps={processSteps}
      technologyCategories={technologyCategories}
      techTitle="Platforms"
      techDescription="We work with your existing stack and recommend additions where they add value."
      qualification={qualification}
      faqs={faqs}
      finalCtaTitle="Ready to accelerate?"
      finalCtaDescription="Let's discuss how we can build a demand generation engine that scales."
      finalCtaButtonText="Start a serious conversation"
    />
  );
};

export default SaaSAcceleration;
